'use client';

import React, { PropsWithChildren, useCallback, useEffect } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider as ReduxProvider } from 'react-redux';
import { LocalizationProvider } from '@ianesthesie-kit/i18n';
import { Intercom, ThemeProvider } from '@/containers';
import { appStore, type RootState } from '@/store';
import { AvailableLocale } from '@ianesthesie-kit/config';
import {
  BaseDialogType,
  DialogContentMap,
  DialogProvider,
  SnackbarProvider,
  useCurrentUser,
  UserProvider
} from '@ianesthesie-kit/redux';
import { AppConfigService, appDialogMap } from '@/config';
import { SentryService } from '@ianesthesie-kit/sentry';
import pick from 'lodash/fp/pick';
import { useSearchParams } from 'next/navigation';
import { useAppDispatch } from '@/hooks';
import { SplashScreenLoader } from '@/components/loader';
import { register as registerTimeAgoLocale } from 'timeago.js';
import timeAgoFrLocale from 'timeago.js/lib/lang/fr';
import { updateTabState } from '@/modules/ui';
import {
  useRouterPush,
  useInitializeLocaleCookie
} from '@ianesthesie-kit/core';
import { DevVersionTag } from '@ianesthesie-kit/version-tag';
import { SnackbarProvider as NotistackProvider } from 'notistack';
import { AsaSnack, MedsAutomationSnack } from '@/components/notistack';
import { useCurrentHospital } from '@/modules/appContext';
import { AuthRedirectListener } from '@/components/authRedirectListener';
import { useChunkLoadErrorHandler } from '@ianesthesie-kit/hooks';

// ---------- helpers moved/adapted from _app ----------
const snackbarStateSelector = (state: RootState) => state.ui.snackbar;
const dialogStateSelector = (state: RootState) => state.ui.dialog;

registerTimeAgoLocale('fr', timeAgoFrLocale);

function isAllowedReturnPath(returnPath = ''): boolean {
  if (!returnPath || /^https?:\W+$/.test(returnPath)) {
    return false;
  }
  return [
    '/',
    '/auth/',
    '/patients',
    '/pending-patients',
    '/prescriptions',
    '/tutorial',
    '/uqr'
  ].some((pref) => returnPath.startsWith(pref));
}

const InitializationLayer = (props: PropsWithChildren) => {
  const user = useCurrentUser();
  const {
    queryResult: { currentData: hospital }
  } = useCurrentHospital({ skip: !user });

  useEffect(() => {
    const partialUser =
      user &&
      pick(['id', 'type', 'phoneNumberVerified', 'emailVerified'], user);
    SentryService.getInstance()
      .setUser(partialUser)
      .addBreadcrumb({
        level: 'info',
        data: partialUser ?? undefined,
        message: `Sentry user set: ${user?.id}`
      });
  }, [user]);

  useEffect(() => {
    if (hospital) {
      SentryService.getInstance().addBreadcrumb({
        level: 'info',
        data: hospital,
        message: `Current hospital: ${hospital?.name}`
      });
    }
  }, [hospital]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const onTabFocusChange = () => {
      dispatch(
        updateTabState({
          focus: document.hasFocus(),
          visibility: document.visibilityState
        })
      );
    };

    window.addEventListener('visibilitychange', onTabFocusChange);
    window.addEventListener('focus', onTabFocusChange);
    window.addEventListener('blur', onTabFocusChange);

    return () => {
      window.removeEventListener('visibilitychange', onTabFocusChange);
      window.removeEventListener('focus', onTabFocusChange);
      window.removeEventListener('blur', onTabFocusChange);
    };
  }, [dispatch]);

  return <>{props.children}</>;
};

export type MainProvidersProps = PropsWithChildren<{
  initialMessages: Record<string, string>;
  locale: AvailableLocale;
}>;

// ---------- Main Providers (replaces MyApp) ----------
export function MainProviders({
  initialMessages,
  locale,
  children
}: MainProvidersProps) {
  // read query params
  const searchParams = useSearchParams();
  const returnPath = searchParams?.get('returnPath') ?? '/';
  const push = useRouterPush();

  const onLogin = useCallback(async () => {
    const path = isAllowedReturnPath(returnPath as string) ? returnPath : '/';
    return push(path as string);
  }, [push, returnPath]);

  const onLogout = useCallback(
    async () => push(`/auth/login?returnPath=${location.pathname}`),
    [push]
  );

  useInitializeLocaleCookie();
  useEffect(() => {
    AppConfigService.getInstance().updateLocale(locale as AvailableLocale);
  }, [locale]);

  const isDev = AppConfigService.getInstance().env === 'dev';

  // Auto-reload on chunk load errors (stale deployment)
  useChunkLoadErrorHandler({
    enabled: !isDev
  });

  return (
    <ReduxProvider store={appStore}>
      <ThemeProvider>
        <CssBaseline />
        <UserProvider
          onLogin={onLogin}
          onLogout={onLogout}
          loader={SplashScreenLoader}
        >
          <InitializationLayer>
            <Intercom locale={locale}>
              <LocalizationProvider
                locale={locale}
                initialMessages={initialMessages}
                devMode={isDev}
                ignoreMissingPattern={/^questionnaire\..*\.group$/}
              >
                <NotistackProvider
                  autoHideDuration={5000}
                  maxSnack={4}
                  Components={{
                    asa: AsaSnack,
                    medsAutomation: MedsAutomationSnack
                  }}
                />

                <SnackbarProvider selector={snackbarStateSelector}>
                  <DialogProvider
                    selector={dialogStateSelector}
                    dialogContentMap={
                      appDialogMap as DialogContentMap<BaseDialogType>
                    }
                  >
                    <div style={{ width: '100%' }}>
                      <AuthRedirectListener />
                      {children}
                      <DevVersionTag config={AppConfigService.getInstance()} />
                    </div>
                  </DialogProvider>
                </SnackbarProvider>
              </LocalizationProvider>
            </Intercom>
          </InitializationLayer>
        </UserProvider>
      </ThemeProvider>
    </ReduxProvider>
  );
}
